import { normValidation } from './input.js';
export class GridRowEdit {
    /** @internal */ rowEditOn() { return !!this.opts.rowEditMode && !!this.opts.editable && !this.srm; }
    /** @internal */ rowEditLeave() { const m = this.opts.rowEditMode; return typeof m === 'object' && m && m.leave ? m.leave : 'commit'; }
    /** 행 편집 중인 물리 행 (없으면 -1) */
    getRowEditRow() { return this.rowEdit ? this.rowEdit.row : -1; }
    /** 행 편집 버퍼 (컬럼 → 값, 아직 Dataset 에 안 들어감) */
    getRowEditValues() { return this.rowEdit ? Object.fromEntries(this.rowEdit.vals) : {}; }
    /** 행 편집 시작 (편집 시작·체크가 부름. 코드에서 직접 불러도 됨) */
    startRowEdit(row) {
        if (!this.rowEditOn() || row < 0 || !this.ds.isVisibleRow(row))
            return false;
        if (this.rowEdit?.row === row)
            return true;
        if (this.rowEdit && !this.saveRowEdit())
            return false;
        const ds = this.ds, vals = new Map();
        // 버퍼 값을 보여 주는 대리: 검증·editRule·계산 컬럼이 편집 중 값을 봄 (나머지는 원래 Dataset 그대로)
        const proxy = Object.create(ds);
        proxy.get = (r, col) => (r === row && vals.has(col) ? vals.get(col) : ds.get(r, col));
        proxy.getRow = (r) => { const o = ds.getRow(r); if (r === row)
            for (const [k, v] of vals)
                o[k] = v; return o; };
        this.rowEdit = { row, vals, proxy };
        try {
            this.opts.onRowEditStart?.({ row, grid: this });
        }
        catch (err) {
            console.error(err);
        }
        this.bump();
        return true;
    }
    /** 편집·체크 값을 버퍼에 (쓰기 전 이벤트는 여기서 — 셀을 확정할 때) */
    /** @internal */ bufferRowEdit(changes, source) {
        const row = changes[0].row;
        if (!this.startRowEdit(row)) {
            this.setCellByUser(changes, this.t('편집'), source, { noBuffer: true });
            return;
        }
        const kept = this.beforeChanges(changes, source), re = this.rowEdit;
        for (const ch of kept) {
            const k = ch.row + ':' + ch.col;
            if (Object.is(ch.value, this.ds.get(row, ch.col)) || (ch.value == null && this.ds.get(row, ch.col) == null))
                re.vals.delete(ch.col);
            else
                re.vals.set(ch.col, ch.value);
            this.errors.delete(k);
            this.warns.delete(k);
        }
        this.bump();
    }
    /** 행 하나 검증 (필수·validate·maxBytes — 버퍼 값 기준). 오류만 돌려주고 경고·안내는 칸에 표시 */
    /** @internal */ rowEditErrors(row) {
        const out = [];
        const ds = this.dsFor(row);
        for (const c of this.cols) {
            if (c.special || c.def.value)
                continue;
            const v = ds.get(row, c.id), rule = this.ruleOf(c, row);
            let m = null;
            if ((rule?.required ?? c.def.required) && (v == null || v === ''))
                m = { level: 'error', message: `${c.def.header ?? c.id}은(는) 필수입니다` };
            else {
                const b = this.bytesError(c, v);
                m = b ? { level: 'error', message: b } : normValidation(c.def.validate?.(v, row, ds));
            }
            this.setSyncMsg(row, c.id, m);
            if (m?.level === 'error')
                out.push({ row, col: c.id, message: m.message, level: 'error' });
        }
        return out;
    }
    /**
     * 행 편집 저장: 열린 편집기 확정 → 검증(validate=false 면 건너뜀) → 버퍼를 Dataset 에 한 번에 (실행 취소 한 건, onCellChange·비동기 검증).
     * 오류면 첫 오류 칸으로 가서 false. 편집 중인 행이 없으면 true
     */
    saveRowEdit(validate = true) {
        const re = this.rowEdit;
        if (!re)
            return true;
        if (this.editing && this.editing.row === re.row && !this.commitEdit())
            return false;
        if (validate) {
            const errs = this.rowEditErrors(re.row);
            if (errs.length) {
                this.bump();
                const vi = this.disp.indexOf(re.row), ci = this.cols.findIndex(c => c.id === errs[0].col);
                if (vi >= 0 && ci >= 0) {
                    this.focus = { vi, ci };
                    this.anchor = { ...this.focus };
                    this.ensureVisible(vi, ci);
                }
                this.positionIme();
                this.showTip(errs[0].message);
                return false;
            }
        }
        const changes = [...re.vals].map(([col, value]) => ({ row: re.row, col, value }));
        const keep = new Map([...this.warns].filter(([k]) => k.startsWith(re.row + ':')));
        re.saving = true;
        this.rowEdit = null;
        try {
            if (changes.length)
                this.setCellByUser(changes, this.t('행 편집'), 'edit', { noBefore: true, noBuffer: true });
        }
        finally {
            re.saving = false;
        }
        for (const [k, w] of keep)
            this.warns.set(k, w); // 저장 전 검증의 경고는 남김
        try {
            this.opts.onRowEditEnd?.({ row: re.row, committed: true, grid: this });
        }
        catch (err) {
            console.error(err);
        }
        this.bump();
        return true;
    }
    /** 행 편집 취소: 버퍼 버림 (Dataset 그대로) */
    cancelRowEdit() {
        const re = this.rowEdit;
        if (!re)
            return false;
        if (this.editing)
            this.cancelEdit();
        this.dropRowEdit(false);
        this.hideTip();
        return true;
    }
    /** @internal */ dropRowEdit(committed) {
        const re = this.rowEdit;
        if (!re)
            return;
        this.rowEdit = null;
        for (const col of re.vals.keys()) {
            const k = re.row + ':' + col;
            this.errors.delete(k);
            this.warns.delete(k);
        }
        try {
            this.opts.onRowEditEnd?.({ row: re.row, committed, grid: this });
        }
        catch (err) {
            console.error(err);
        }
        this.bump();
    }
    /** 다른 행으로 옮기기 전 (setFocus): 정책대로. false 면 지금은 옮기지 않음 (confirm 은 창을 띄우고 고르면 다시 옮김) */
    /** @internal */ leaveRowEdit(vi, ci, extend) {
        const re = this.rowEdit;
        if (!re)
            return true;
        if (this.editing && this.editing.row === re.row && !this.commitEdit())
            return false;
        if (!re.vals.size) {
            this.dropRowEdit(false);
            return true;
        }
        const pol = this.rowEditLeave();
        if (pol === 'cancel') {
            this.dropRowEdit(false);
            return true;
        }
        if (pol === 'commit')
            return this.saveRowEdit();
        void this.confirmDialog(this.t('행 편집'), this.t('이 행의 변경을 저장할까요?'), [{ id: 'save', label: this.t('저장'), primary: true }, { id: 'discard', label: this.t('버림') }, { id: 'stay', label: this.t('취소') }], 'og-rowc').then(id => {
            if (id === 'save') {
                if (this.saveRowEdit())
                    this.setFocus(vi, ci, extend);
            }
            else if (id === 'discard') {
                this.dropRowEdit(false);
                this.setFocus(vi, ci, extend);
            }
            this.focusGrid();
        });
        return false;
    }
    /** 행 오른쪽 저장·취소 막대 (✔ ✕) 위치 */
    /** @internal */ placeRowEditBar(vTop) {
        const re = this.rowEdit;
        let bar = this.rowEditBar;
        const vi = re ? this.disp.indexOf(re.row) : -1;
        if (!re || vi < 0) {
            if (bar && !bar.hidden)
                bar.hidden = true;
            return;
        }
        if (!bar) {
            bar = document.createElement('div');
            bar.className = 'og-reb';
            bar.setAttribute('role', 'toolbar');
            bar.setAttribute('aria-label', this.t('행 편집'));
            const mk = (a, sym, title) => { const b = document.createElement('button'); b.type = 'button'; b.dataset.a = a; b.textContent = sym; b.title = title; b.setAttribute('aria-label', title); bar.appendChild(b); };
            mk('save', '✔', this.t('행 저장 (Ctrl+Enter)'));
            mk('cancel', '✕', this.t('행 취소 (Esc)'));
            bar.addEventListener('mousedown', e => {
                e.preventDefault();
                e.stopPropagation();
                const a = e.target.closest('button')?.dataset.a;
                if (a === 'save')
                    this.saveRowEdit();
                else if (a === 'cancel')
                    this.cancelRowEdit();
                this.focusGrid();
            });
            this.bodyPanes[1].parentElement.appendChild(bar);
            this.rowEditBar = bar;
        }
        const main = this.bodyPanes[1], y = this.rowTop(vi) - vTop, h = this.rowH(vi);
        if (y + h < 0 || y > main.clientHeight) {
            if (!bar.hidden)
                bar.hidden = true;
            return;
        }
        bar.hidden = false;
        bar.style.top = Math.max(0, y) + 'px';
        bar.style.height = h + 'px';
        bar.style.right = this.paneWidth[2] + (main.offsetWidth - main.clientWidth) + 2 + 'px';
    }
}
